<template>
  <div>
    <v-layout>
      <v-flex>
        <v-btn color="primary" @click="showAddItemDialog">
          <v-icon dark>add</v-icon> {{ addItemBtnText }}
        </v-btn>
        <v-btn v-if="isEditMode" color="secondary" :disabled="loading" @click.stop="cancelEditData()">
          {{ $t('cancel') }}
        </v-btn>
        <!-- <v-btn color="primary" :disabled="loading" @click.stop="isEditMode ? saveEditData() : enterEditData()">
          {{$t(isEditMode ? 'save' : 'edit')}}
        </v-btn> -->
      </v-flex>
      <v-spacer></v-spacer>
      <v-flex xs6 sm4 md2>
        <v-autocomplete
          :items="warehousesSites"
          item-text="siteName"
          item-key="key"
          return-object
          v-model="currentWarehouseSite"
          :label="$t('warehouseSite')"
          :disabled="isEditMode"
          :hint="$t('selectASiteBeforeEditLocation')"
          persistent-hint
        ></v-autocomplete>
      </v-flex>
      <v-spacer></v-spacer>
      <v-flex v-if="!isEditMode">
        <v-layout>
          <v-text-field
            prepend-icon="view_week"
            :label="$t('barcode')"
            single-line
            hide-details
            v-model="barcode"
            clearable
          ></v-text-field>
          <v-btn color="primary" @click="printBarcode">
            <v-icon dark>print</v-icon> 
          </v-btn>
        </v-layout>
      </v-flex>
      <v-spacer></v-spacer>
      <v-flex xs5 md3>
        <v-text-field
          append-icon="search"
          :label="$t('search')"
          single-line
          hide-details
          v-model="search"
          clearable
          @keyup.enter="filterEnter"
        ></v-text-field>
      </v-flex>
    </v-layout>
    <v-layout align-baseline justify-start>
      <v-flex class="success--text caption"><span class="realtime-border px-1">{{ $t('realTIME') }}</span></v-flex>
    </v-layout>
    <v-data-table
      :headers="headers"
      :items="items"
      :search="search"
      :loading="loading"
      :pagination.sync="pagination"
      :rows-per-page-items="rowPerPage"
      class="elevation-1"
      >
      <template v-slot:progress>
        <v-progress-linear color="blue" indeterminate></v-progress-linear>
      </template>
      <template v-slot:items="props">
        <td class="text-xs-left" v-if="mode === 'upc'">{{ props.item.upc }}</td>
        <td class="text-xs-left" v-else>{{ props.item.sku }}</td>
        <td class="text-xs-left" v-if="mode === 'sku'">{{ organizationKeyToId.get(props.item.organizationKey) }}</td>
        <td class="text-xs-left">
          <template v-if="isEditMode">
            <v-text-field
              v-model="props.item.description"
              ></v-text-field>
          </template>
          <template v-else>
            {{ props.item.description }}
          </template>
        </td>
        <td class="text-xs-left">
          <template v-if="isEditMode">
            <v-combobox
              v-model="props.item.location"
              chips
              multiple
              deletable-chips
              clearable
              :disabled="!currentWarehouseSite.key"
            ></v-combobox>
          </template>
          <template v-else>
            {{ props.item.location && props.item.location.join(', ') }}
          </template>
        </td>
        <td class="text-xs-left">
          <template v-if="isEditMode">
            <v-select
              v-model="props.item.size"
              :items="sizes"
              item-text="name"
              item-value="sortKey"
              :disabled="loading"
              ></v-select>
            <v-text-field
              v-if="props.item.size === 'custom'"
              v-model.number="props.item.unitFee"
              prefix="$"
              solo></v-text-field>
          </template>
          <template v-else>{{ getFeeString(props.item) }}</template>
        </td>
        <td class="text-xs-left">
          {{ props.item.snTemplate }}
        </td>
        <td class="text-xs-left">
          <v-flex>
            <v-chip v-for="item of getPricingPlanFromUpc(props.item.upc)" :key="item">{{item}}</v-chip>
          </v-flex>
        </td>
        <td>
          <v-layout justify-center>
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <v-btn v-on="on" icon flat color="primary" :disabled="JSON.stringify(props.item.approvedSkus) === '{}'" @click="showSkuDetailDialog(props.item)">
                  <v-icon>info</v-icon>
                </v-btn>
              </template>
              <span> {{ $t('approvedSKUs') }} </span>
            </v-tooltip>
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <v-btn v-on="on" icon flat color="primary" :disabled="!currentWarehouseSite.key || isEditMode"  @click="showUPCDescEditor(props.item)">
                  <v-icon>edit</v-icon>
                </v-btn>
              </template>
              <span> {{ $t('editUPC') }} </span>
            </v-tooltip>
            <v-btn v-if="!isEditMode" dark color="red" icon flat @click.stop="deleteItem(props.item)">
              <v-icon dark>cancel</v-icon>
            </v-btn>
          </v-layout>
        </td>
      </template>
      <div slot="no-results" class="text-xs-center">
        <v-icon>warning</v-icon>
        {{ $t('upcSearchNoResult', { search }) }}
      </div>
    </v-data-table>
    <SimpleTextPopup
      :title="$t('approvedSKUs')"
      v-model="showApprovedSkus"
      @popupClose="showApprovedSkus = false"
      :hideRgtBtn="true"
      medium>
      <template v-slot:input>
        <v-container fluid>
          <v-data-table
            :headers="[
              { text: $t('index'), sortable: false, value: 'upc' },
              { text: $t('organization'), sortable: false, value: 'upc', width: '15%' },
              { text: $t('skus'), sortable: false, value: 'keywordForSearch' }
            ]"
            :items="selectedUpcDetails"
            class="levation-2 myDense"
            :pagination.sync="pagination"
            item-key="upc"
            :loading="isDeletingSku"
            :rows-per-page-items="rowPerPage">
            <template v-slot:items="props">
              <td class="subheading">{{ props.index + 1}}</td>
              <td class="text-xs-left">{{ organizationKeyToId.get(props.item.organizationKey) }}</td>
              <td class="text-xs-left">
                <v-layout>
                  <v-chip v-for="sku in props.item.approvedSkus" :key="sku" close @input="removeSku(sku, props.item)">
                    {{ sku }} 
                  </v-chip>
                </v-layout>
              </td>
            </template>
          </v-data-table>
        </v-container>
      </template>
    </SimpleTextPopup>
    <FormSubmitPopup
      :title="$t('editUPC')"
      v-model="showEditUPCDesc"
      @popupClose="showEditUPCDesc = false"
      :rightMethod="updateUPC"
      medium
    >
      <template v-slot:input>
        <v-layout>
          <v-text-field
            name="upcDesc"
            :label="$t('description')"
            v-model="editingUPCItem.description"
          ></v-text-field>
        </v-layout>
        <v-layout>
          <v-combobox
            v-model="editingUPCItem.location"
            :label="$t('location')"
            chips
            multiple
            deletable-chips
            clearable
            :disabled="!currentWarehouseSite.key"
          ></v-combobox>
        </v-layout>
        <v-layout>
          <v-text-field
            :label="$t('snTemplate')"
            v-model="editingUPCItem.snTemplate"
          ></v-text-field>
        </v-layout>
        <v-layout>
          <v-flex sm4 mr-2>
            <v-select
              v-model="editingUPCItem.size"
              :items="sizes"
              :label="$t('size')"
              item-text="name"
              item-value="sortKey"
              :disabled="loading"
              ></v-select>
          </v-flex>
          <v-flex sm3  mr-2>
            <v-text-field
              v-if="editingUPCItem.size === 'custom'"
              v-model.number="editingUPCItem.inboundFee"
              :label="$t('inboundFee')"
              prefix="$"
              ></v-text-field>
          </v-flex>
          <v-flex sm3 mr-2>
            <v-text-field
              v-if="editingUPCItem.size === 'custom'"
              v-model.number="editingUPCItem.outboundFee"
              :label="$t('outboundFee')"
              prefix="$"
              ></v-text-field>
          </v-flex>
          <v-flex sm3>
            <v-text-field
              v-if="editingUPCItem.size === 'custom'"
              v-model.number="editingUPCItem.storageFee"
              :label="$t('storageFee')"
              prefix="$"
              ></v-text-field>
          </v-flex>
        </v-layout>
      </template>
    </FormSubmitPopup>
  </div>
</template>

<script>
import SimpleTextPopup from './SimpleTextPopup'
import FormSubmitPopup from './FormSubmitPopup'
import { saveBarcodeImage, deepEqual, mediaTools, cloneDeep, checkRules } from '@/utils/tools'

export default {
  name: 'UpcMainTabItem',
  components: {
    SimpleTextPopup,
    FormSubmitPopup
  },
  mixins: [mediaTools, checkRules],
  data () {
    return {
      mode: 'upc',
      addItemBtnText: this.$t('addUPC'),
      toastColor: 'error',
      toastEnable: false,
      toastText: '',
      editUpcDialog: false,
      deleteUpcDialog: false,
      toastMsgs: [],
      search: '',
      rowPerPage: [15, 30, 50, {text: this.$t('all'), value: -1}],
      pagination: {
        sortBy: 'upc'
      },
      headers: [
        { text: this.$t('upc'), sortable: false, value: 'upc', width: '15%' },
        { text: this.$t('description'), sortable: false, value: 'description' },
        { text: this.$t('location'), sortable: false, value: 'location' },
        { text: this.$t('size'), sortable: false, value: 'size', width: '20%' },
        { text: this.$t('snTemplate'), sortable: false, value: 'snTemplate', width: '10%' },
        { text: this.$t('pricingPlan'), sortable: false, value: 'pricingPlan', width: '10%' },
        { text: this.$t('actions'), sortable: false, align: 'center', value: 'keywordForSearch', width: '10%' }
      ],
      isEditMode: false,
      cachedItems: [],
      loading: false,
      tab: 'SKU',
      tabs: [
        'UPC', 'SKU'
      ],
      model: 'tab-2',
      selectedUpcDetails: [],
      showApprovedSkus: false,
      barcode: '',
      currentWarehouseSite: {},
      showEditUPCDesc: false,
      editingUPCItem: {},
      inShowUpcItem: {},
      isDeletingSku: false,
      snTemplate: ''
    }
  },
  mounted () {
    this.currentWarehouseSite = this.warehousesSites.find(item => item.key === this.defaultSite) || {}
  },
  computed: {
    items () {
      return this.isEditMode ? this.cachedItems : this.upcs
    },
    upcs () {
      return Object.values(this.$store.getters.upcMap)
        .map(item => {
          let location = []
          if (item.location && item.location.length) {
            location = (item.location.find(loc => loc.siteKey === this.currentWarehouseSite.key) || {}).location || []
          }
          return {...item, location}
        })
    },
    sizes () {
      return this.$store.getters.upcSizes
    },
    sortKeyToSize () {
      return new Map([
        ...this.sizes.map(item => [item.sortKey, item.name]),
        ...this.sizes.map(item => [item.name, item.name])
      ])
    },
    sizeToSortKey () {
      return new Map([
        ...this.sizes.map(item => [item.name, item.sortKey]),
        ...this.sizes.map(item => [item.sortKey, item.sortKey]),
        ['custom', 'custom']
      ])
    },
    organizationKeyToId () {
      return this.$store.getters.organizationKeyToId
    },
    warehousesSites () {
      return this.$store.getters.warehousesSites
    },
    defaultSite () {
      return window.localStorage.getItem('defaultSite' + this.$store.getters.uid)
    },
    warehousePricingPlans () {
      return this.$store.getters.warehousePricingPlans.filter(plan => plan.upcs) || []
    }
  },
  methods: {
    showAddItemDialog () {
      this.$emit('showAddUpcDialog')
    },
    filterEnter (event) {
      event.target.select()
      this.hintSound()()
    },
    showSkuDetailDialog (item) {
      this.inShowUpcItem = item
      this.showApprovedSkus = true
      this.selectedUpcDetails = Object.keys(item.approvedSkus).map(organizationKey => {
        return {
          organizationKey,
          approvedSkus: item.approvedSkus[organizationKey]
        }
      })
    },
    hasChangedItems () {
      return this.cachedItems.filter(upcObj => { return upcObj.upc && this.$store.getters.upcMap[upcObj.upc].size !== upcObj.size }) > 0
    },
    enterEditData () {
      if (!this.currentWarehouseSite.key) return alert(this.$t('selectSiteEdit'))
      this.cachedItems = this.upcs.map(upc => { 
        let {size} = upc
        if (size !== undefined && size !== null) {
          size = this.sizeToSortKey.get(upc.size)
          return {...cloneDeep(upc), size}
        } else {
          return cloneDeep(upc)
        }
      })
      if (!this.cachedItems) this.cachedItems = []
      this.isEditMode = true
    },
    leaveEditMode () {
      this.isEditMode = false
      this.cachedItems = []
      this.loading = false
    },
    cancelEditData () {
      if (this.hasChangedItems() && !confirm(this.$t('confirmCancelAllChanges'))) return
      this.leaveEditMode()
    },
    saveEditData () {
      if (this.hasChangedItems() && !confirm(this.$t('confirmSaveAllChanges'))) return
      if (this.cachedItems.some(upc => (upc.size === 'custom' && !upc.inboundFee && !upc.outboundFee && !upc.storageFee))) {
        return alert(this.$t('customSizeUpcsMustHaveUnitFee'))
      }
      this.loading = true
      let changedUpc = this.cachedItems.filter(upcObj => {
        let originUpcObj = {...this.$store.getters.upcMap[upcObj.upc]}
        if (originUpcObj.location && originUpcObj.location.length) {
          originUpcObj.location = (originUpcObj.location.find(loc => loc.siteKey === this.currentWarehouseSite.key) || {}).location
        }

        if (!deepEqual(originUpcObj, upcObj)) {
          // console.log('changed upc: ', originUpcObj, upcObj)
          return true
        } else {
          return false
        }
      })
      console.log('total changed UPC: ', changedUpc.length)
      if (changedUpc.length === 0) {
        this.leaveEditMode()
        return
      }
      if (changedUpc.length > 50) {
        this.$store.dispatch('showToast', {info: this.$t('tooManyUpdates'), level: 'error'})
        return
      }
      return Promise.all(changedUpc.map(upcObj => {
        return this.$store.dispatch('updateUpc', {...upcObj, siteKey: this.currentWarehouseSite.key})
      }))
        .then(() => {
          this.leaveEditMode()
        })
    },
    deleteItem (payload) {
      if (confirm(this.$t('confirmDeleteUpc'))) {
        this.$store.dispatch('deleteUpc', payload)
          .catch(error => {
            this.$store.dispatch('showToast', {info: error.message, level: 'error'})
          })
      }
    },
    getFeeString (item) {
      if (!item.size && item.size !== 0) return ''
      if (item.size === 'custom' && item.inboundFee) {
        return `${item.size}($${item.inboundFee}) ($${item.outboundFee || 0}) ($${item.storageFee || 0})`
      }
      return this.sortKeyToSize.get(item.size)
    },
    printBarcode () {
      if (!this.barcode) return alert(this.$t('pleaseEnterBarcode'))
      saveBarcodeImage(this.barcode, this.barcode, false)
    },
    getSiteNameFromKey (siteKey) {
      return (this.warehousesSites.find(ele => ele.key === siteKey) || {}).siteName
    },
    showUPCDescEditor (item) {
      this.showEditUPCDesc = true
      this.editingUPCItem = {...item, snTemplate: item.snTemplate || ''}
    },
    updateUPC () {
      return this.$store.dispatch('updateUpc', {...this.editingUPCItem, siteKey: this.currentWarehouseSite.key})
        .finally(() => {
          this.editingUPCItem = {}
        })
    },
    async removeSku (sku, item) {
      this.isDeletingSku = true
      if (confirm(this.$t('confirmRemoveSth', {field: 'sku'}))) {
        const { key, upc, keywordForSearch } = this.inShowUpcItem
        const { organizationKey } = item
        const warehouseKey = this.$store.getters.activeWarehouse
        return this.$store.dispatch('removeApprovedSku', {
          key,
          sku,
          upc,
          tenantKey: organizationKey,
          warehouseKey,
          keywordForSearch
        }).then(() => {
          this.isDeletingSku = false
          this.selectedUpcDetails[0].approvedSkus = this.selectedUpcDetails[0].approvedSkus.filter(approvedSku => approvedSku !== sku)
        }).catch(error => {
          this.isDeletingSku = false
          console.log(error.message)
          throw error
        })
      } else {
        this.isDeletingSku = false
      }
    },
    getPricingPlanFromUpc (upc) {
      let matchedPlan = this.warehousePricingPlans.filter(plan => plan.upcs.some(upcInfo => upcInfo.upc === upc))
      return matchedPlan.map(plan => plan.name)
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/UpcMainTabItem.vue